/**
 * Fast-drive signal accessors for Playwright-backed browser sessions.
 *
 * @module
 */

import type { Page } from 'playwright';
import type { BrowserConsoleErrorSummary, BrowserDomSignals } from '../browser-client.interface.js';
import type { CapturedConsoleMessage } from './internal-types.js';

export type RaceAgainstSignal = <T>(
  stage: string,
  signal: AbortSignal | undefined,
  fn: () => Promise<T>
) => Promise<T>;

export async function readFastDriveDomRevision(args: {
  getPage: () => Promise<Page>;
  raceAgainstSignal: RaceAgainstSignal;
  signal?: AbortSignal;
}): Promise<number> {
  const { getPage, raceAgainstSignal, signal } = args;
  try {
    const page = await getPage();
    const rev = await raceAgainstSignal('dom-revision', signal, () =>
      page.evaluate(() => (window as unknown as { __domRevision?: number }).__domRevision ?? 0)
    );
    return typeof rev === 'number' ? rev : 0;
  } catch {
    // Page navigating/closed or signal aborted - treat as no reading.
    return 0;
  }
}

export async function readFastDriveDomSignals(args: {
  getPage: () => Promise<Page>;
  raceAgainstSignal: RaceAgainstSignal;
  signal?: AbortSignal;
}): Promise<BrowserDomSignals> {
  const empty = { rev: 0, dialogOpenTs: 0, dialogName: '' };
  const { getPage, raceAgainstSignal, signal } = args;
  try {
    const page = await getPage();
    const sig = await raceAgainstSignal('dom-signals', signal, () =>
      page.evaluate(() => {
        const w = window as unknown as {
          __domRevision?: number;
          __lastDialogOpenTs?: number;
          __lastDialogName?: string;
        };
        return {
          rev: w.__domRevision ?? 0,
          dialogOpenTs: w.__lastDialogOpenTs ?? 0,
          dialogName: w.__lastDialogName ?? '',
        };
      })
    );
    return sig ?? empty;
  } catch {
    // Page navigating/closed or signal aborted - treat as no reading.
    return empty;
  }
}

export function getFastDriveOpenPageUrls(page: Page | null | undefined): string[] {
  try {
    const context = page?.context();
    if (!context) return [];
    return context.pages().map((p) => {
      try {
        return p.url();
      } catch {
        return '';
      }
    });
  } catch {
    return [];
  }
}

export function getFastDriveConsoleErrorSummarySince(
  messages: readonly CapturedConsoleMessage[],
  sinceMs: number
): BrowserConsoleErrorSummary {
  try {
    const errors = messages.filter((m) => m.type === 'error' && m.timestampMs >= sinceMs);
    if (errors.length === 0) return { count: 0 };
    return { count: errors.length, first: errors[0]?.text };
  } catch {
    return { count: 0 };
  }
}
